import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    Wrench,
    LogOut,
    Inbox,
    PhoneCall,
    CheckCircle2,
    Trash2,
    Search,
    ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import {
    Tabs,
    TabsList,
    TabsTrigger,
} from "../components/ui/tabs";
import { api, formatApiErrorDetail } from "../lib/api";

const STATUSES = [
    { value: "new", label: "New", className: "bg-[#00E5FF]/15 text-[#00E5FF] border-[#00E5FF]/30" },
    { value: "contacted", label: "Contacted", className: "bg-[#E5FE40]/15 text-[#E5FE40] border-[#E5FE40]/30" },
    { value: "resolved", label: "Resolved", className: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
];

function statusMeta(s) {
    return STATUSES.find((x) => x.value === s) ?? STATUSES[0];
}

export default function AdminDashboard() {
    const nav = useNavigate();
    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("fixhub_user") || "null");
        } catch {
            return null;
        }
    }, []);
    const [quotes, setQuotes] = useState([]);
    const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, resolved: 0 });
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [q, s] = await Promise.all([
                api.get("/admin/quotes", { params: filter !== "all" ? { status: filter } : {} }),
                api.get("/admin/stats"),
            ]);
            setQuotes(q.data);
            setStats(s.data);
        } catch (err) {
            if (err?.response?.status === 401) {
                localStorage.clear();
                nav("/admin/login", { replace: true });
            } else {
                toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const logout = () => {
        localStorage.clear();
        nav("/admin/login", { replace: true });
    };

    const updateStatus = async (id, status) => {
        try {
            const { data } = await api.patch(`/admin/quotes/${id}`, { status });
            setQuotes((qs) => qs.map((q) => (q.id === id ? data : q)));
            // refresh stats
            api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
            toast.success("Status updated");
            if (detail?.id === id) setDetail(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this quote request? This cannot be undone.")) return;
        try {
            await api.delete(`/admin/quotes/${id}`);
            setQuotes((qs) => qs.filter((q) => q.id !== id));
            setDetail(null);
            api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
            toast.success("Deleted");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
        }
    };

    const visible = quotes.filter((q) => {
        if (!search.trim()) return true;
        const s = search.toLowerCase();
        return (
            q.name.toLowerCase().includes(s) ||
            q.email.toLowerCase().includes(s) ||
            q.phone.toLowerCase().includes(s) ||
            q.device.toLowerCase().includes(s) ||
            q.issue.toLowerCase().includes(s)
        );
    });

    const cards = [
        { label: "Total requests", value: stats.total, icon: Inbox, accent: "text-white" },
        { label: "New", value: stats.new, icon: Inbox, accent: "text-[#00E5FF]" },
        { label: "Contacted", value: stats.contacted, icon: PhoneCall, accent: "text-[#E5FE40]" },
        { label: "Resolved", value: stats.resolved, icon: CheckCircle2, accent: "text-emerald-300" },
    ];

    return (
        <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard">
            <header className="sticky top-0 z-30 bg-black/60 backdrop-blur-xl border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-sm bg-[#00E5FF] text-black">
                            <Wrench className="h-4 w-4" />
                        </span>
                        <div>
                            <div className="font-display font-bold leading-none">Mr Mobile Admin</div>
                            <div className="text-xs text-zinc-500 mt-1">Quote requests console</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <Link
                            to="/"
                            data-testid="view-site"
                            className="text-sm text-zinc-400 hover:text-white transition-colors hidden sm:inline-flex items-center gap-1.5"
                        >
                            View site <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                        <div className="text-sm text-zinc-300 hidden md:block">{user?.email}</div>
                        <button
                            onClick={logout}
                            data-testid="logout-btn"
                            className="inline-flex items-center gap-2 text-sm border border-white/15 px-3.5 py-1.5 rounded-sm hover:border-white/40 transition-colors"
                        >
                            <LogOut className="h-4 w-4" /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
                    <div>
                        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">Quote Requests</h1>
                        <p className="text-zinc-400 mt-2 text-sm">All inbound enquiries from your marketing site.</p>
                    </div>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                    {cards.map((c) => {
                        const I = c.icon;
                        return (
                            <div
                                key={c.label}
                                data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="rounded-lg border border-white/10 bg-[#0A0A0F] p-6"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-xs uppercase tracking-[0.18em] text-zinc-500">{c.label}</span>
                                    <I className={`h-4 w-4 ${c.accent}`} />
                                </div>
                                <div className={`font-display text-3xl font-bold mt-3 ${c.accent}`}>{c.value}</div>
                            </div>
                        );
                    })}
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0A0A0F]">
                    <div className="p-4 md:p-5 border-b border-white/10 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                        <Tabs value={filter} onValueChange={setFilter}>
                            <TabsList className="bg-white/5 border border-white/10">
                                <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
                                <TabsTrigger value="new" data-testid="tab-new">New</TabsTrigger>
                                <TabsTrigger value="contacted" data-testid="tab-contacted">Contacted</TabsTrigger>
                                <TabsTrigger value="resolved" data-testid="tab-resolved">Resolved</TabsTrigger>
                            </TabsList>
                        </Tabs>
                        <div className="relative w-full md:max-w-xs">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                            <Input
                                data-testid="search-input"
                                placeholder="Search name, device, email…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-[#00E5FF] focus-visible:ring-[#00E5FF]"
                            />
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-white/10 hover:bg-transparent">
                                    <TableHead className="text-zinc-400">Customer</TableHead>
                                    <TableHead className="text-zinc-400">Device / Issue</TableHead>
                                    <TableHead className="text-zinc-400">Contact</TableHead>
                                    <TableHead className="text-zinc-400">Received</TableHead>
                                    <TableHead className="text-zinc-400">Status</TableHead>
                                    <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-12">Loading…</TableCell></TableRow>
                                ) : visible.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center text-zinc-500 py-12" data-testid="empty-state">
                                            No quote requests yet.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    visible.map((q) => {
                                        const sm = statusMeta(q.status);
                                        return (
                                            <TableRow
                                                key={q.id}
                                                data-testid={`quote-row-${q.id}`}
                                                className="border-white/10 hover:bg-white/[0.03] cursor-pointer"
                                                onClick={() => setDetail(q)}
                                            >
                                                <TableCell className="font-medium text-white">{q.name}</TableCell>
                                                <TableCell className="text-zinc-300">
                                                    <div className="text-white">{q.device}</div>
                                                    <div className="text-xs text-zinc-500">{q.issue}</div>
                                                </TableCell>
                                                <TableCell className="text-zinc-300">
                                                    <div>{q.phone}</div>
                                                    <div className="text-xs text-zinc-500">{q.email}</div>
                                                </TableCell>
                                                <TableCell className="text-zinc-400 text-sm">
                                                    {new Date(q.created_at).toLocaleString()}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className={`${sm.className} font-medium`}>{sm.label}</Badge>
                                                </TableCell>
                                                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                                    <div className="flex items-center justify-end gap-2">
                                                        <Select value={q.status} onValueChange={(v) => updateStatus(q.id, v)}>
                                                            <SelectTrigger
                                                                data-testid={`status-select-${q.id}`}
                                                                className="w-[140px] h-9 bg-white/5 border-white/10 text-white"
                                                            >
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent className="bg-[#0A0A0F] border-white/10 text-white">
                                                                {STATUSES.map((s) => (
                                                                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                        <button
                                                            data-testid={`delete-${q.id}`}
                                                            onClick={() => remove(q.id)}
                                                            className="h-9 w-9 inline-flex items-center justify-center rounded-sm border border-white/10 text-zinc-400 hover:text-red-400 hover:border-red-400/40 transition-colors"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </main>

            <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
                <DialogContent className="bg-[#0A0A0F] border-white/10 text-white max-w-lg" data-testid="quote-detail-dialog">
                    {detail && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display text-xl">{detail.name}</DialogTitle>
                                <DialogDescription className="text-zinc-400">
                                    Submitted {new Date(detail.created_at).toLocaleString()}
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-3 text-sm">
                                <Row k="Email" v={detail.email} />
                                <Row k="Phone" v={detail.phone} />
                                <Row k="Device" v={detail.device} />
                                <Row k="Issue" v={detail.issue} />
                                {detail.description && (
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-2">Description</div>
                                        <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{detail.description}</p>
                                    </div>
                                )}
                                <div>
                                    <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-2">Status</div>
                                    <Select value={detail.status} onValueChange={(v) => updateStatus(detail.id, v)}>
                                        <SelectTrigger className="bg-white/5 border-white/10 text-white">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#0A0A0F] border-white/10 text-white">
                                            {STATUSES.map((s) => (
                                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <DialogFooter>
                                <button
                                    onClick={() => setDetail(null)}
                                    className="border border-white/15 px-4 py-2 rounded-sm text-sm hover:border-white/40 transition-colors"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={() => remove(detail.id)}
                                    className="bg-red-500/15 border border-red-500/40 text-red-300 px-4 py-2 rounded-sm text-sm hover:bg-red-500/25 transition-colors"
                                >
                                    Delete
                                </button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex justify-between gap-6 border-b border-white/5 pb-2">
            <span className="text-zinc-500">{k}</span>
            <span className="text-white text-right">{v}</span>
        </div>
    );
}
